import { isDayjs } from 'dayjs'
import * as yup from 'yup'

function requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string) {
  return function (this: yup.TestContext<yup.Maybe<yup.AnyObject>>, value: unknown) {
    // Check all levels in the context hierarchy to find the target field
    const foundLevel = this.from?.find((level) => {
      const levelData = level?.value ?? {}
      return formFieldName in levelData
    })

    if (!foundLevel) return true
    const levelData = foundLevel.value!
    const foundTargetValue = levelData[formFieldName]

    // Only validate if the target field matches the expected value
    if (foundTargetValue === targetValue) {
      let isInvalid = value === undefined || value === null || value === ''

      // Handle Dayjs objects - check if they're invalid
      if (isDayjs(value)) {
        isInvalid = !value.isValid()
      }

      if (isInvalid) {
        return this.createError({ message })
      }

      return true
    }

    return true
  }
}

declare module 'yup' {
  interface StringSchema {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  interface NumberSchema {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  interface BooleanSchema {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  // Generics must match Yup.ArraySchema's signature for declaration merging
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  interface ArraySchema<TIn extends any[] | null | undefined, TContext, TDefault, TFlags> {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  interface MixedSchema {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  interface DateSchema {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }

  // This interface requires the type to match exactly as defined in yup
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ObjectSchema<TIn extends yup.Maybe<yup.AnyObject> | null | undefined, TContext, TDefault, TFlags> {
    /**
     * @description Required when the form field is the target value
     * @param formFieldName - The name of the form field
     * @param targetValue - The value of the form field
     * @param message - The message to display if the field is required
     * @returns The schema
     */
    requiredWhenFieldIs(formFieldName: string, targetValue: unknown, message: string): this
  }
}

// Attach to various schema types
yup.addMethod(
  yup.string,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

yup.addMethod(
  yup.number,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

yup.addMethod(
  yup.boolean,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

yup.addMethod(
  yup.array,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

yup.addMethod(yup.date, 'requiredWhenFieldIs', function (formFieldName: string, targetValue: unknown, message: string) {
  return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
})

yup.addMethod(
  yup.object,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

yup.addMethod(
  yup.mixed,
  'requiredWhenFieldIs',
  function (formFieldName: string, targetValue: unknown, message: string) {
    return this.test('required-when-form-field-is', message, requiredWhenFieldIs(formFieldName, targetValue, message))
  }
)

export {}
